import React from 'react';
import { DeliveryDetails } from '../../types/delivery-details';
import {
  Application,
  Catalogue,
  Delivery,
  getCatalogueItemFromCatalogue,
  isVirtualDevice,
  splitAddressByDevice,
} from 'src/features/common';
import { DeliveryOptions } from 'src/features/common/types/application/delivery-options';
import { Col, Colors, Row, Text } from 'src/common';
import { ShowIf, RevampAccordion, RevampAccordionItem } from 'src/components';
import { DeliveryAddressListModal } from '../delivery-address-list-modal/delivery-address-list-modal';
import { ApplicationContext } from 'src/features/order/context';
import { getThirdPartyDefaultSolutions } from 'src/features/common/helpers/delivery-options';

interface IAddressItem {
  deliveryDetails: DeliveryDetails;
  catalogue: Catalogue;
  application: Application;
  onAddressEdit: () => void;
  onChangeAddress: (
    address: string,
    productDetails: {
      devices: string;
      image: string;
      quantity: number;
    },
  ) => void;
  onAddNewAddress: () => void;
}

interface Address {
  addressLines: string[];
  deliveryOptions?: DeliveryOptions[];
  deliveryOption?: string;
  onChangeDeliveryOption: (value: DeliveryOptions) => void;
  productDetails: {
    devices: string;
    image: string;
    quantity: number;
  };
}
interface GroupedAddress {
  default: Address[];
  deliveries: Address[];
  collections: Address[];
}

enum DeliveryType {
  Default = 'Default',
  Delivery = 'Delivery',
  Collection = 'Collection',
}

const DeliveryItem = (props: {
  addresses: Address[];
  type: DeliveryType;
  defaultAddress?: Delivery;
  editDefaultDelivery?: () => void;
  onChangeAddress?: (
    address: string,
    productDetails: {
      devices: string;
      image: string;
      quantity: number;
    },
  ) => void;
  onAddNewAddress?: () => void;
  onShowAddressList?: () => void;
}) => {
  const devices = React.useMemo(() => {
    return props.addresses.filter((address) => !isVirtualDevice(address.productDetails.devices));
  }, [props.addresses]);

  return props.addresses.length ? (
    <Col
      gap={'12px'}
      margin={'12px 0 0 0'}
      css={`
        hr {
          width: 100% !important;
        }
      `}
    >
      <ShowIf condition={props.type === DeliveryType.Default}>
        <Row display="flex" gap={'10px'}>
          <Text
            cursor={'pointer'}
            as={'p'}
            color={Colors.Red}
            fontSize={'14px'}
            onClick={() => props.onShowAddressList && props.onShowAddressList()}
            textDecoration={'underline'}
          >
            My address list
          </Text>
          |
          <Text
            cursor={'pointer'}
            as={'p'}
            fontSize={'14px'}
            color={Colors.Red}
            onClick={() => props.onAddNewAddress && props.onAddNewAddress()}
            textDecoration={'underline'}
          >
            Add new address
          </Text>
        </Row>
        <Col>
          <Text as={'p'} margin={'0 0 4px 0'} fontWeight={700} fontSize={'14px'}>
            Delivery address
          </Text>
          <Row flexWrap={'wrap'}>
            <Text
              flex={'3'}
              fontSize={'16px'}
              overflow={'hidden'}
              textOverflow={'ellipsis'}
              whiteSpace={'nowrap'}
              as={'p'}
            >
              {props.addresses?.[0]?.addressLines.join(', ')}
            </Text>
            |
            <Text
              textDecoration={'underline'}
              cursor={'pointer'}
              fontSize={'14px'}
              margin={'0 0 0 10px'}
              color={Colors.Red}
              onClick={() => props.editDefaultDelivery && props.editDefaultDelivery()}
              as={'p'}
            >
              Change
            </Text>
          </Row>
          <Col margin={'5px 0'}>
            <Text as={'p'}>{props.defaultAddress?.address.suburb}</Text>
            <Text as={'p'}>{props.defaultAddress?.address.province}</Text>
            <Text as={'p'}>{props.defaultAddress?.address.postalCode}</Text>
          </Col>
          <Col gap={'5px'} margin={'10px 0'}>
            <Text fontSize={'14px'} fontWeight={700}>
              Recipient name
            </Text>
            <Text>{props.defaultAddress?.receiverDetails.managerName}</Text>
          </Col>
          <Col>
            <Text margin={'0 0 5px 0'} fontSize={'14px'} fontWeight={700}>
              Devices
            </Text>
            {devices.map((address, idx) => (
              <Text fontSize={'16px'} key={idx}>
                {`${address.productDetails.quantity}x ${address.productDetails.devices}`}
              </Text>
            ))}
          </Col>
        </Col>
      </ShowIf>
      <ShowIf condition={props.type !== DeliveryType.Default}>
        <Col>
          {props.addresses.map((address, idx) => (
            <Col
              margin={'0 0 5px 0'}
              backgroundColor={Colors.Grey25}
              key={idx}
              padding={'10px 20px'}
            >
              <Row>
                <Col overflow={'hidden'} flex={1}>
                  {props.type === 'Delivery' && (
                    <Text
                      padding="0 20px 0 0"
                      flex={'1'}
                      overflow={'hidden'}
                      fontSize={'14px'}
                      fontWeight={500}
                      lineHeight={'21px'}
                      textOverflow={'ellipsis'}
                      whiteSpace="nowrap"
                    >
                      {address.addressLines.join(', ')}
                    </Text>
                  )}
                  <Text fontSize={'14px'} fontWeight={400} lineHeight={'21px'}>
                    {`${address.productDetails.quantity}x ${address.productDetails.devices}`}
                  </Text>
                </Col>
                <Text
                  fontSize={'14px'}
                  cursor={'pointer'}
                  textDecoration={'underline'}
                  fontWeight={400}
                  lineHeight={'19px'}
                  onClick={() =>
                    props.onChangeAddress &&
                    props.onChangeAddress(address.addressLines.join(', '), address.productDetails)
                  }
                  as={'p'}
                >
                  Change
                </Text>
              </Row>
            </Col>
          ))}
        </Col>
      </ShowIf>
    </Col>
  ) : (
    <></>
  );
};

export const AddressSummaryAddressItem = (props: IAddressItem) => {
  const [showAddressModal, setShowAddressModal] = React.useState(false);
  const { application, catalogue } = React.useContext(ApplicationContext);
  const addresses: GroupedAddress = React.useMemo(() => {
    const addressesNew: GroupedAddress = {
      default: [],
      deliveries: [],
      collections: [],
    };
    if (props.deliveryDetails.defaultAddress) {
      // filter out tender types
      const defaultAddressSolutions = props.deliveryDetails.defaultAddress.solutions.filter(
        (solution) => {
          const catalogueItem = getCatalogueItemFromCatalogue(
            props.catalogue,
            solution.productId,
            solution.acquiringChannel,
          );

          return (
            catalogueItem &&
            (!catalogueItem.device.productCategoryName || catalogueItem.device.isProductCategory)
          );
        },
      );

      const defaultAddressSolutionsFiltered = getThirdPartyDefaultSolutions(
        defaultAddressSolutions,
        application.thirdPartyDefaultAddressSolutions || [],
        catalogue.products,
      );

      const defaultDelivery: Delivery = {
        ...props.deliveryDetails.defaultAddress,
        solutions: defaultAddressSolutionsFiltered,
      };

      const addresses: Address[] = splitAddressByDevice(defaultDelivery, props.catalogue, () => {});

      const collectable = addresses.filter(
        (address) =>
          !!address?.deliveryOption &&
          address.deliveryOption === DeliveryOptions.Stock &&
          !isVirtualDevice(address.productDetails.devices),
      );

      const deliverable = addresses.filter(
        (address) =>
          !!address?.deliveryOption &&
          address.deliveryOption !== DeliveryOptions.Stock &&
          !isVirtualDevice(address.productDetails.devices),
      );

      if (collectable.length) {
        addressesNew.collections.push(...collectable);
      }

      addressesNew.default = deliverable;
    }

    if (props.deliveryDetails.customAddresses) {
      addressesNew.deliveries = [];

      props.deliveryDetails.customAddresses.forEach((delivery, index) => {
        if (!delivery.solutions?.[0]?.productId) return;

        const addresses: Address[] = splitAddressByDevice(
          delivery,
          props.catalogue,
          (deliveryOption: string) => {
            const newDeliveries = [...(props.application.opportunity?.deliveries || [])];
            const address: Delivery = newDeliveries?.[index] as Delivery;
            address.deliveryOption = deliveryOption;
          },
        );

        for (const address of addresses) {
          if (delivery.deliveryOption !== DeliveryOptions.Stock) {
            addressesNew.deliveries.push(address);
            continue;
          }

          addressesNew.collections.push(address);
        }
      });
    }

    return addressesNew;
  }, [props.deliveryDetails]);

  return (
    <>
      <DeliveryAddressListModal
        onClose={() => setShowAddressModal(false)}
        visible={showAddressModal}
        onEditButtonClicked={() => {}}
        onAddAddress={() => {
          setShowAddressModal(false);
          props.onAddNewAddress && props.onAddNewAddress();
        }}
      />

      <RevampAccordion>
        {addresses.default.length > 0 ? (
          <RevampAccordionItem
            className="!px-0 [&_.header]:bg-white [&_.header]:p-0 border-solid border-b-2 border-silver-100 [&_.body]:bg-white [&_.body]:border-none"
            title="Default delivery address"
          >
            <DeliveryItem
              onShowAddressList={() => setShowAddressModal(true)}
              type={DeliveryType.Default}
              defaultAddress={props.deliveryDetails.defaultAddress}
              addresses={addresses.default}
              editDefaultDelivery={props.onAddressEdit}
              onAddNewAddress={props.onAddNewAddress}
            />
          </RevampAccordionItem>
        ) : (
          <></>
        )}

        {addresses.deliveries.length > 0 ? (
          <RevampAccordionItem
            className="!px-0 [&_.header]:bg-white [&_.header]:p-0 border-solid border-b-2 border-silver-100 [&_.body]:bg-white [&_.body]:border-none"
            title="Deliveries"
          >
            <DeliveryItem
              type={DeliveryType.Delivery}
              addresses={addresses.deliveries}
              onChangeAddress={props.onChangeAddress}
            />
          </RevampAccordionItem>
        ) : (
          <></>
        )}
        {addresses.collections.length > 0 ? (
          <RevampAccordionItem
            className="!px-0 [&_.body]:!px-0 [&_.body]:!py-0 [&_.header]:!py-4 [&_.header]:!h-fit [&_.header]:bg-white [&_.header]:!p-0 border-solid border-b-2 border-silver-100 [&_.body]:bg-white [&_.body]:border-none"
            title="Collections"
          >
            <DeliveryItem
              type={DeliveryType.Collection}
              addresses={addresses.collections}
              onChangeAddress={props.onChangeAddress}
            />
          </RevampAccordionItem>
        ) : (
          <></>
        )}
      </RevampAccordion>
    </>
  );
};
